import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  Group as GroupIcon,
  Info as InfoIcon,
  ManageAccounts as ManageAccountsIcon,
  MonetizationOn as MonetizationOnIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { ItemDetailsLayout } from 'src/components/common';
import { DemoDashboardEntityToggle } from 'src/components/demoDashboardMode';
import { EmployerControlledByInfo } from 'src/components/employer';
import { ModalId } from 'src/constants/modals';
import { useEmployerById, useEmployerLogo, useEmployerParamId } from 'src/hooks/employer';
import { useModalV2 } from 'src/hooks/shared';
import { EmployerControlledByConfigurationModal } from 'src/modals/employer';
import { MenuItem } from 'src/types/menu';
import { employerReceivesOwnRevenueShare } from 'src/utils/revenueShare';
import { buildUrl } from 'src/utils/router';
export const EmployersDetailLayoutCVStaff: React.FC<React.PropsWithChildren> = ({ children }) => {
  const employerId = useEmployerParamId();
  const { openModal } = useModalV2();

  const { employer, loading } = useEmployerById(employerId);
  const { employerLogo, employerLogoLoading, updateEmployerLogo, employerLogoUpdating } =
    useEmployerLogo(employerId);

  const builtRoutes = React.useMemo(
    () => ({
      companyInformation: buildUrl(appRoutes.employers.detail.companyInformation, {
        [RouterEntitiesId.employer]: employerId,
      }),
      disclaimers: buildUrl(appRoutes.employers.detail.disclaimers, {
        [RouterEntitiesId.employer]: employerId,
      }),
      wallet: buildUrl(appRoutes.employers.detail.wallet, {
        [RouterEntitiesId.employer]: employerId,
      }),
      staff: buildUrl(appRoutes.employers.detail.staff, {
        [RouterEntitiesId.employer]: employerId,
      }),
      employees: buildUrl(appRoutes.employers.detail.employees, {
        [RouterEntitiesId.employer]: employerId,
      }),
      settings: buildUrl(appRoutes.employers.detail.settings, {
        [RouterEntitiesId.employer]: employerId,
      }),
    }),
    [employerId],
  );

  const menuItems: Array<MenuItem> = React.useMemo(
    () => [
      {
        link: builtRoutes.companyInformation,
        title: 'Company Information',
        icon: AccountBalanceIcon,
      },
      {
        link: builtRoutes.disclaimers,
        title: 'Employer Disclaimer',
        icon: InfoIcon,
      },
      {
        link: builtRoutes.wallet,
        title: 'Billing',
        icon: MonetizationOnIcon,
        hide: !employer || !employerReceivesOwnRevenueShare(employer),
      },
      {
        link: builtRoutes.staff,
        title: 'Staff',
        icon: ManageAccountsIcon,
      },
      {
        link: builtRoutes.employees,
        title: 'Employees',
        icon: GroupIcon,
      },
      {
        link: builtRoutes.settings,
        title: 'Settings',
        icon: SettingsIcon,
      },
    ],
    [builtRoutes, employer],
  );

  const handleOpenEmployerControlledByConfigurationModal = React.useCallback(() => {
    if (!employerId) return;
    openModal(EmployerControlledByConfigurationModal, {
      id: ModalId.EmployerControlledByConfiguration,
      employerId,
    });
  }, [openModal, employerId]);

  return (
    <ItemDetailsLayout
      name={employer?.displayName}
      logoUrl={employerLogo?.downloadUrl || DEFAULT_AVATAR}
      menuItems={menuItems}
      subtitleComponent={<EmployerControlledByInfo employerId={employerId} />}
      logoProps={{
        onChange: updateEmployerLogo,
        loading: employerLogoLoading || employerLogoUpdating,
      }}
      loading={loading}
      onSettingsClick={handleOpenEmployerControlledByConfigurationModal}
      sidebarFooter={<DemoDashboardEntityToggle entityType="employers" entityId={employerId} />}
    >
      <Box>{children}</Box>
    </ItemDetailsLayout>
  );
};
